import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  makeStyles,
  tokens,
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from '@fluentui/react-components'
import { People16Regular } from '@fluentui/react-icons'
import { EditableField } from '../../components/EditableField'
import { EditableList } from '../../components/EditableList'
import { apiErrorMessage } from '../../lib/apiError'
import { SkillTypeaheadChipList } from '../../components/SkillTypeaheadChipList'
import { PersonPicker } from '../../components/PersonPicker'
import { ApplicationsTab } from './ApplicationsTab'
import { useDemandInvitations, useWithdrawInvitation } from '../../hooks/useInvitations'
import { useAddTeamMember } from '../../hooks/useDemands'
import { useTalentDirectory } from '../../hooks/useTalentDirectory'
import { useCapabilities } from '../../hooks/useCapabilities'
import { useToast } from '../../components/ToastProvider'
import type { MentionableUser } from '../../hooks/useMentionableUsers'
import type { Demand } from '../../types/demand'
import type { Application } from '../../types/application'
import { activatable } from '../../lib/activatable'
import { bayerColors, surfaces } from '../../theme'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    padding: tokens.spacingVerticalL,
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: 0,
    marginBottom: tokens.spacingVerticalM,
  },
  subtitle: {
    marginTop: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalS,
  },
  skills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  needsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: tokens.spacingVerticalS,
  },
  effortBand: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    background: surfaces.proposalCardBg,
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  effortLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
  },
})

export interface TalentTabProps {
  demand: Demand
  applications: Application[]
  isCouncil: boolean
  /** FA-scoped edit flag: admin, or council whose FA matches the demand's. */
  canEdit: boolean
  onSave: (path: string, value: string | string[] | number) => void
}

/**
 * Talent tab — combines What we need + effort, Skills,
 * and Applications into one cohesive view.
 */
export function TalentTab({ demand, applications, isCouncil, canEdit, onSave }: TalentTabProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const toast = useToast()
  // For marking grandfathered chips; cached and shared with the profile.
  const { data: catalog } = useCapabilities()
  const { data: invitations } = useDemandInvitations(isCouncil ? demand.id : undefined)
  const withdrawMutation = useWithdrawInvitation()
  const pendingInvitations = (invitations ?? []).filter((i) => i.status === 'pending')
  const [retracting, setRetracting] = useState<(typeof pendingInvitations)[number] | null>(null)

  // Manually add a team member — bypasses advertise/apply/approve. Allowed
  // across the active middle band (the whole point is to skip advertising).
  const canAddMember =
    canEdit &&
    (demand.stage === 'scoping' ||
      demand.stage === 'triaging' ||
      demand.stage === 'resourcing')
  const addMember = useAddTeamMember()
  const { data: talentDir } = useTalentDirectory({ enabled: canAddMember })
  const talentOptions: MentionableUser[] = (talentDir?.items ?? []).map((t) => ({
    id: t.id,
    name: t.name,
    email: t.email,
    role: 'talent',
  }))

  function handleAddMember(args: { talentId?: number; name?: string }) {
    addMember.mutate(
      { demandId: demand.id, ...args },
      {
        onSuccess: () => toast.showToast({ title: 'Contributor added' }),
        onError: (err: unknown) => {
          toast.showToast({ title: apiErrorMessage(err, 'Failed to add contributor'), intent: 'error' })
        },
      },
    )
  }

  function handleSkillsSave(_path: string, values: string[]) {
    onSave('skills', values)
  }

  function handleNeedsSave(_path: string, values: string[]) {
    // Only persist non-empty items to backend
    const trimmed = values.map((s) => s.trim()).filter(Boolean)
    onSave('requirements', trimmed.join('\n'))
  }

  const needs = (demand.requirements ?? '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)

  const effortValue = [
    demand.hours_per_week ? `${demand.hours_per_week} hrs/wk` : null,
    demand.duration ? demand.duration : null,
  ]
    .filter(Boolean)
    .join(' · ')

  // The pane follows the applications, not the stage (#1251).
  //
  // It used to render only while `stage === 'resourcing'`, and `ApplicationsTab`
  // is the ONLY component in the frontend that calls `useUpdateApplicationStatus`
  // — so moving a demand back to Triaging (which the stage dialog offers as the
  // first natural next step from Resourcing, no written reason required) took
  // the approve/reject controls off the page and left the applicants pending
  // forever. Nothing notifies them either: closing a demand tells its
  // applicants, a regress or a park tells nobody.
  const hasUndecided = applications.some((a) => a.status === 'pending')
  const showApplications = demand.stage === 'resourcing' || hasUndecided

  const canInvite = isCouncil && demand.stage === 'resourcing'

  return (
    <div className={styles.root}>
      {/* What we need + effort */}
      <section className={styles.section} data-testid="talent-needs">
        <div className={styles.needsRow}>
          <h3 className={styles.sectionTitle} style={{ margin: 0 }}>
            What we need
          </h3>
        </div>
        {isCouncil ? (
          <EditableList
            values={needs}
            path="requirements"
            onSave={handleNeedsSave}
            placeholder="A specific need…"
            addLabel="+ Add a need"
          />
        ) : needs.length === 0 ? (
          <Caption1>No specific needs articulated yet.</Caption1>
        ) : (
          <ul>
            {needs.map((n, idx) => (
              <li key={idx} style={{ fontSize: 14, marginBottom: 4 }}>
                {n}
              </li>
            ))}
          </ul>
        )}
        <div className={styles.effortBand}>
          <span className={styles.effortLabel}>Anticipated effort</span>
          {isCouncil ? (
            <>
              <span style={{ fontSize: 12 }}>
                <EditableField
                  value={demand.hours_per_week != null ? String(demand.hours_per_week) : ''}
                  path="hours_per_week"
                  onSave={(p, v) => onSave(p, v ? Number(v) : 0)}
                  placeholder="e.g. 12"
                  ariaLabel="Hours per week"
                />
                &nbsp;hrs/wk
              </span>
              <span style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>·</span>
              <span style={{ fontSize: 12 }}>
                <EditableField
                  value={demand.duration ?? ''}
                  path="duration"
                  onSave={onSave}
                  placeholder="e.g. 6 weeks"
                  ariaLabel="Duration"
                />
              </span>
            </>
          ) : (
            <span style={{ fontSize: 13 }}>
              {effortValue || 'TBD'}
            </span>
          )}
        </div>
      </section>

      {/* Skills */}
      <section className={styles.section} data-testid="talent-skills">
        <h3 className={styles.sectionTitle}>Skills</h3>
        {isCouncil ? (
          <SkillTypeaheadChipList
            values={demand.skills ?? []}
            onSave={(values) => handleSkillsSave('skills', values)}
            addLabel="+ Add skill"
            catalog={catalog?.map((c) => c.name)}
          />
        ) : (demand.skills ?? []).length === 0 ? (
          <Caption1>No skills specified yet.</Caption1>
        ) : (
          <div className={styles.skills}>
            {(demand.skills ?? []).map((s) => (
              <Badge key={s} appearance="tint" color="informative" size="small">
                {s}
              </Badge>
            ))}
          </div>
        )}
      </section>

      {canInvite && (
        <Button
          appearance="primary"
          icon={<People16Regular />}
          onClick={() => navigate(`/talents?inviteFor=${demand.id}`)}
          data-testid="invite-talents-btn"
        >
          Invite talents
        </Button>
      )}

      {canAddMember && (
        <section className={styles.section} data-testid="add-team-member">
          <h3 className={styles.sectionTitle}>Add contributor</h3>
          <Caption1 className={styles.subtitle}>
            Skips applications. Anyone can be added, even outside the directory.
          </Caption1>
          <PersonPicker
            currentName={null}
            roleLabel="contributor"
            avatarColor="#0f766e"
            users={talentOptions}
            allowFreehand
            disabled={addMember.isPending}
            onSelect={(u) => {
              if (u) handleAddMember({ talentId: u.id })
            }}
            onFreehandSelect={(name) => handleAddMember({ name })}
          />
        </section>
      )}

      {isCouncil && pendingInvitations.length > 0 && (
        <section className={styles.section} data-testid="pending-invitations">
          <h3 className={styles.sectionTitle}>Pending Invitations</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingInvitations.map((inv) => (
              <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Badge appearance="tint" color="warning" size="small">Pending</Badge>
                <span
                  style={{ fontSize: 13, cursor: 'pointer', color: bayerColors.navyMid }}
                  role="link"
                  {...activatable(() => navigate(`/talents/${inv.talent_id}`))}
                >
                  {inv.talent_name ?? `Talent #${inv.talent_id}`}
                </span>
                <Caption1 style={{ marginLeft: 'auto' }}>
                  {new Date(inv.created_at).toLocaleDateString()}
                </Caption1>
                {/* The one removal in #1245 that gets a confirmation rather
                    than an undo, because it is the one that is not cheaply
                    reversible: retracting sends the talent a retraction
                    notification (routers/invitations.py, the dispatch in
                    withdraw_invitation), and re-inviting sends another. An
                    "undo" would mean three messages to a real person for one
                    mis-click — worse than the mis-click. So: ask first, and
                    say what the click will do to them. */}
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => setRetracting(inv)}
                  disabled={withdrawMutation.isPending}
                  style={{ color: '#c4314b', fontSize: 12 }}
                  data-testid={`retract-invitation-${inv.id}`}
                >
                  Retract
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {showApplications && (
        <ApplicationsTab demand={demand} applications={applications} />
      )}

      <Dialog
        open={retracting !== null}
        onOpenChange={(_, data) => {
          if (!data.open) setRetracting(null)
        }}
        modalType="alert"
      >
        <DialogSurface data-testid="retract-invitation-confirm">
          <DialogBody>
            <DialogTitle>
              Retract the invitation to{' '}
              {retracting?.talent_name ?? `Talent #${retracting?.talent_id}`}?
            </DialogTitle>
            <DialogContent>
              They get an email saying it is withdrawn.
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setRetracting(null)}
                data-testid="retract-invitation-cancel"
              >
                Keep the invitation
              </Button>
              <Button
                appearance="primary"
                disabled={withdrawMutation.isPending}
                data-testid="retract-invitation-confirm-btn"
                onClick={() => {
                  const target = retracting
                  if (!target) return
                  setRetracting(null)
                  withdrawMutation.mutate(target.id, {
                    onSuccess: () => toast.showToast({ title: 'Invitation retracted' }),
                    onError: (err: unknown) => {
                      toast.showToast({
                        title: apiErrorMessage(err, 'Failed to retract invitation'),
                        intent: 'error',
                      })
                    },
                  })
                }}
              >
                Retract
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
